export enum DescriptionOption {
  ALL = 'ALL',
  OUTGOING = 'OUTGOING',
  INCOMING = 'INCOMING',
}

export enum ARDOTAttribute {
  TAG_NUMBER = 'Tag Number',
  DESCRIPTION = 'Description',
  OPERATOR_NAME = 'Operator Name',
  COMMUTE_STATUS = 'Commute Status',
  MONITORED_STATUS = 'Monitored Status',
}

export enum ColumnType {
  NAME = 'NAME',
  ASSET_NAME = 'ASSET_NAME',
  SCHEMA_NAME = 'SCHEMA_NAME',
  FROM_ASSET_NAME = 'FROM_ASSET_NAME',
  STATUS = 'STATUS',
  DESCRIPTION = 'DESCRIPTION',
  QUANTITY = 'QUANTITY',
  ASSIGNED_TO = 'ASSIGNED_TO',
  CREATED_BY = 'CREATED_BY',
  CREATED_DATE = 'CREATED_DATE',
  UPDATED_DATE = 'UPDATED_DATE',
  REPLENISHMENTS = 'REPLENISHMENTS',
  QUANTITY_ORDERED = 'QUANTITY_ORDERED',
  QUANTITY_RECEIVED = 'QUANTITY_RECEIVED',
  CATEGORY_PATH = 'CATEGORY_PATH',
  TO_CATEGORY_PATH = 'TO_CATEGORY_PATH',
  REVIEWER = 'REVIEWER',
  RECEIVER = 'RECEIVER',
  RESOLUTION_DATE = 'RESOLUTION_DATE',
  ASSIGNEES = 'ASSIGNEES',
}

export interface Column {
  type: ColumnType;
  label: string;
  sortValue?: string;
}

export interface ConfirmModalContent {
  title: string;
  content: string;
}

export enum AssetRequestType {
  ARDOT_118_TRANSFER = 'ARDOT_118_TRANSFER',
  SDDOT_ADJUSTMENT = 'SDDOT_ADJUSTMENT',
  SDDOT_REPLENISHMENT = 'SDDOT_REPLENISHMENT',
  SDDOT_INVENTORY_TRANSFER = 'SDDOT_INVENTORY_TRANSFER',
  SDDOT_STOCKPILE_TRANSFER = 'SDDOT_STOCKPILE_TRANSFER',
  ADOT_INVENTORY_TRANSFER = 'ADOT_INVENTORY_TRANSFER',
  ADOT_REPLENISHMENT = 'ADOT_REPLENISHMENT',
  ADOT_ADJUSTMENT = 'ADOT_ADJUSTMENT',
}

// AssetRequestStatus definition contains all possible status for requests.
// Request status are per request type.
export enum AssetRequestStatus {
  CANCELED = 'CANCELED',
  REJECTED = 'REJECTED',

  // ARDOT
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',

  // SDDOT
  FULLY_RECEIVED = 'FULLY_RECEIVED',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  PENDING = 'PENDING',
  FULL_APPROVAL = 'FULL_APPROVAL',
  PARTIAL_APPROVAL = 'PARTIAL_APPROVAL',
}

export interface Replenishment {
  id: string;
  name: string;
  quantityReceived: number;
  unitCost: number;
  totalCost: number;
  invoiceNumber: string;
  comments?: string;
  dateReceived?: number;
  createdDate: number;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  updatedDate?: number;
  updatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface AssetRequestAttribute {
  id: string;
  name: string;
  oldValue: any;
  newValue: any;
}

export type AssetRequestUser = {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
};

export type AssetRequestUserGroup = {
  id: string;
  name: string;
  colorId: number;
};

export type AssetRequestPurchaseUnit = {
  unit: string;
  conversionNumber: number;
  baseUnit: string;
};

export type AssetRequestPurchaseUnitInput = {
  unit: string;
  conversionNumber: number;
};

// AssetRequest definition contains all properties for all types of requests.
// Request types are per tenant, and will only utilize specific properties.
export interface AssetRequest {
  id: string;
  type: AssetRequestType;
  name: string;
  assetId: string;
  assetName?: string;
  status?: AssetRequestStatus;
  comments?: string;
  createdDate: number;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  updatedDate?: number;
  updatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };

  // ARDOT
  schemaName?: string;
  attributes?: AssetRequestAttribute[];
  categoryId?: string;
  categoryPath?: string;
  toCategoryId?: string;
  toCategoryPath?: string;
  reviewer?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  receiver?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  reviewDate?: number;
  reviewBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  receiveDate?: number;
  receiveBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  cancelDate?: number;
  resolutionDate?: number;

  // SDDOT
  assetUnit?: string;
  quantity?: number;
  quantityOrdered?: number;
  quantityApproved?: number;
  reason?: string;
  assignedTo?: AssetRequestUser[];
  fromAssetId?: string;
  fromAssetName?: string;
  replenishments?: Replenishment[];

  // ADOT
  assignedToGroups?: AssetRequestUserGroup[];
  purchaseUnit?: AssetRequestPurchaseUnit;
  purchaseOrderNumber?: string;
}

export interface AssetRequestsConnection {
  totalCount: number;
  assetRequests: AssetRequest[];
}

export interface AssetRequestsConnectionInput {
  page: number;
  limit: number;
  type?: AssetRequestType;
  assetId?: string;
  name?: string;
  sortBy?: string;
  isAscending?: boolean;
  statuses?: AssetRequestStatus[];
  createdDateStart?: number;
  createdDateEnd?: number;
  updatedDateStart?: number;
  updatedDateEnd?: number;
  createdBy?: string[];
  categoryIds?: string[];
  schemaIds?: string[];

  // ARDOT
  resolutionDateStart?: number;
  resolutionDateEnd?: number;
  reviewer?: string[];
  receiver?: string[];

  // SDDOT
  fromAssetId?: string;
  assignedTo?: string[];

  // ADOT
  assignedToGroups?: string[];
}

export interface AssetRequestsFilters {
  type?: AssetRequestType;
  assetId?: string;
  name?: string;
  statuses?: AssetRequestStatus[];
  createdDateStart?: number;
  createdDateEnd?: number;
  updatedDateStart?: number;
  updatedDateEnd?: number;
  createdBy?: string[];
  categoryIds?: string[];
  schemaIds?: string[];

  // ARDOT
  resolutionDateStart?: number;
  resolutionDateEnd?: number;
  reviewer?: string[];
  receiver?: string[];

  // SDDOT
  fromAssetId?: string;
  assignedTo?: string[];

  // ADOT
  assignedToGroups?: string[];
}

export interface AssetRequestCreateInput {
  assetId: string;
  type: AssetRequestType;

  // ARDOT
  attributes?: AssetRequestAttribute[];
  categoryId?: string;
  categoryPath?: string;
  toCategoryId?: string;
  toCategoryPath?: string;
  reviewer?: string;
  receiver?: string;

  // SDDOT
  quantity?: number;
  reason?: string;
  comments?: string;
  fromAssetId?: string;
  assignedTo?: string[];
  quantityOrdered?: number;
  replenishment?: {
    quantityReceived: number;
    unitCost: number;
    totalCost: number;
    comments?: string;
    dateReceived?: number;
    invoiceNumber: string;
  };

  // ADOT
  assignedToGroups?: string[];
  purchaseUnit?: AssetRequestPurchaseUnitInput;
  purchaseOrderNumber?: string;
}

export interface AssetRequestUpdateInput {
  id: string;
  status?: AssetRequestStatus;
  comments?: string;

  // SDDOT
  quantityApproved?: number;
}

export interface ReplenishmentCreateInput {
  id: string;
  quantityReceived: number;
  unitCost: number;
  totalCost: number;
  invoiceNumber: string;
  comments?: string;
  dateReceived?: number;
}

export interface ReplenishmentUpdateInput {
  requestId: string;
  replenishmentId: string;
  quantityReceived?: number;
  unitCost?: number;
  totalCost?: number;
  invoiceNumber?: string;
  comments?: string;
  dateReceived?: number;
}
